import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { Player } from '@/lib/schemas';

/* =====================================================================
   Store global — slices:
   - auth:          user de Firebase (Google), uid + email + displayName + photoURL
   - currentPlayer: id + datos del jugador en curso (null si aún no se registró)
   - ui:            toast
===================================================================== */

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface AuthSlice {
  user: AuthUser | null;
  authReady: boolean;
  authError: string | null;
  setUser: (user: AuthUser) => void;
  clearUser: () => void;
  setAuthError: (code: string) => void;
}

interface CurrentPlayerSlice {
  currentPlayerId: string | null;
  currentPlayer: Player | null;
  setCurrentPlayer: (id: string, player: Player) => void;
  clearCurrentPlayer: () => void;
  patchCurrentPlayer: (patch: Partial<Player>) => void;
}

interface ToastSlice {
  toast: { message: string; type: 'success' | 'error' | 'warning' | 'info' } | null;
  showToast: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void;
  clearToast: () => void;
}

type Store = AuthSlice & CurrentPlayerSlice & ToastSlice;

export const useStore = create<Store>()(
  devtools(
    (set) => ({
      // auth
      user: null,
      authReady: false,
      authError: null,
      setUser: (user) => set({ user, authReady: true, authError: null }),
      clearUser: () => set({ user: null, authReady: true, authError: null }),
      setAuthError: (code) => set({ user: null, authReady: true, authError: code }),

      // currentPlayer
      currentPlayerId: null,
      currentPlayer: null,
      setCurrentPlayer: (id, player) => set({ currentPlayerId: id, currentPlayer: player }),
      clearCurrentPlayer: () => set({ currentPlayerId: null, currentPlayer: null }),
      patchCurrentPlayer: (patch) =>
        set((s) => (s.currentPlayer ? { currentPlayer: { ...s.currentPlayer, ...patch } } : s)),

      // toast
      toast: null,
      showToast: (message, type = 'info') => set({ toast: { message, type } }),
      clearToast: () => set({ toast: null }),
    }),
    { name: 'etribe-dc-store', enabled: import.meta.env.DEV },
  ),
);

/* =====================================================================
   Selectores atómicos.
===================================================================== */

export const useUser = () => useStore((s) => s.user);
export const useUid = () => useStore((s) => s.user?.uid ?? null);
export const useAuthReady = () => useStore((s) => s.authReady);
export const useAuthError = () => useStore((s) => s.authError);
export const useCurrentPlayer = () => useStore((s) => s.currentPlayer);
export const useCurrentPlayerId = () => useStore((s) => s.currentPlayerId);
export const useToast = () => useStore((s) => s.toast);
